import React, { useState, useRef, useEffect } from 'react';
import { Button, Box, Typography, IconButton, Chip, CircularProgress, Breadcrumbs, Link, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { FaPlus, FaTimes, FaEdit, FaChevronLeft, FaCheck, FaArrowRight, FaSearch, FaDownload } from 'react-icons/fa';
import DeleteConfirmationDialog from '../../../../components/dialogs/DeleteConfirmationDialog';
import LoadingSubmitButton from '../../../../components/LoadingSubmitButton';
import { cancelButton, confirmButton } from '../../../../components/Buttons';
import { exportProtocolToDocx } from '../utils/docxExport';
import pencilIcon from '../../../../assets/pencil.png';
import trashIcon from '../../../../assets/trash.png';
import personIcon from '../../../../assets/Person.png';
import calendarIcon from '../../../../assets/Calendar.png';
import thermometerIcon from '../../../../assets/Thermometer.png';
import layoutIcon from '../../../../assets/Layout.png';

// ─── Icons ───────────────────────────────────────────────────────────────────

const EditIcon = () => <img src={pencilIcon} alt="ערוך" style={{ width: 24, height: 24, objectFit: 'contain' }} />;
const DeleteIcon = () => <img src={trashIcon} alt="מחק" style={{ width: 24, height: 24, objectFit: 'contain' }} />;

// Helper function to check for Hebrew characters
const hasHebrew = (text) => /[\u0590-\u05FF]/.test(text || '');

// ─── Reusable Components ─────────────────────────────────────────────────────

const ListField = ({ title, items, onAdd, onDelete }) => {
  const [value, setValue] = useState('');

  const handleAdd = () => {
    if (!value.trim()) return;
    onAdd(value);
    setValue('');
  };

  return (
    <Box sx={{ bgcolor: '#fff', p: 2, borderRadius: '6px', border: '1px solid #e0e0e0' }}>
      <Typography sx={{ color: '#103C6E', fontWeight: 700, fontSize: '1.1rem', mb: 1.5 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        <input
          className="modal-input-sm"
          placeholder="הוסף..."
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          onBlur={handleAdd}
        />
        <IconButton size="small" onClick={handleAdd} disabled={!value.trim()} sx={{ bgcolor: '#103C6E', color: '#fff', '&:hover': { bgcolor: '#0d2e54' }, borderRadius: '8px' }}>
          <FaPlus size={12} />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {items.map((item, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, bgcolor: '#fff', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '0.85rem' }}>
            <span style={{ flex: 1, color: '#103C6E' }}>{item}</span>
            <IconButton size="small" onClick={() => onDelete(i)} sx={{ color: '#103C6E', p: 0.5 }}>
              <FaTimes size={10} />
            </IconButton>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const DrugCard = ({ drug, drugs, getDrugDisplayName, onUpdate, onDelete, isLastDrug }) => {
  const drugCardRef = useRef(null);
  const isNewDrug = !drug.drugId;
  const [editing, setEditing] = useState(isNewDrug);
  const [ed, setEd] = useState({
    ...drug,
    dosePerKg: drug.dosePerKg !== undefined ? String(drug.dosePerKg) : '',
    maxDose: drug.maxDose !== undefined ? String(drug.maxDose) : '',
  });

  const showHebrewWarning = hasHebrew(ed.dosage) || hasHebrew(ed.frequency) || hasHebrew(ed.duration) || hasHebrew(ed.howToGive);

  useEffect(() => {
    if (isNewDrug && isLastDrug && editing && drugCardRef.current) {
      setTimeout(() => {
        drugCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
  }, []);

  const handleSave = () => {
    const drugToSave = ed.isPerKg
      ? {
        drugId: ed.drugId,
        dosage: '',
        isPerKg: true,
        dosePerKg: parseFloat(ed.dosePerKg),
        unit: ed.unit || 'mg',
        ...(ed.maxDose !== '' ? { maxDose: parseFloat(ed.maxDose) } : {}),
        frequency: ed.frequency,
        duration: ed.duration,
        comment: ed.comment,
        howToGive: ed.howToGive,
      }
      : {
        drugId: ed.drugId,
        dosage: ed.dosage,
        frequency: ed.frequency,
        duration: ed.duration,
        comment: ed.comment,
        howToGive: ed.howToGive,
      };
    onUpdate(drugToSave);
    setEditing(false);
  };

  const dosageDisplay = drug.isPerKg
    ? `${drug.dosePerKg} ${drug.unit || 'mg'}/kg${drug.maxDose ? ` (עד ${drug.maxDose} ${drug.unit || 'mg'})` : ''}`
    : drug.dosage;

  if (editing) {
    return (
      <Box ref={drugCardRef} sx={{ bgcolor: '#fff', p: 2, borderRadius: '6px', border: '2px solid #103C6E' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 1.5 }}>
          <select
            className="modal-input-sm"
            value={ed.drugId}
            onChange={(e) => setEd({ ...ed, drugId: e.target.value })}
            style={{ gridColumn: '1 / -1' }}
          >
            <option value="">בחר תרופה</option>
            {drugs.map(d => <option key={d.id} value={d.id}>{d.displayName}</option>)}
          </select>
          <label style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', color: '#103C6E' }}>
            <input
              type="checkbox"
              checked={!!ed.isPerKg}
              onChange={(e) => setEd({ ...ed, isPerKg: e.target.checked, dosage: '', dosePerKg: '', maxDose: '' })}
              style={{ accentColor: '#103C6E' }}
            />
            מינון לפי משקל
          </label>
          {ed.isPerKg ? (
            <>
              <input
                className="modal-input-sm"
                placeholder="מינון לק״ג"
                value={ed.dosePerKg || ''}
                onChange={(e) => setEd({ ...ed, dosePerKg: e.target.value })}
                type="number"
                min="0"
                step="0.01"
              />
              <select
                className="modal-input-sm"
                value={ed.unit || 'mg'}
                onChange={(e) => setEd({ ...ed, unit: e.target.value })}
              >
                <option value="mg">mg/kg</option>
                <option value="g">g/kg</option>
                <option value="mcg">mcg/kg</option>
                <option value="units">units/kg</option>
                <option value="mEq">mEq/kg</option>
              </select>
              <input
                className="modal-input-sm"
                placeholder="מינון מקסימלי (אופציונלי)"
                value={ed.maxDose || ''}
                onChange={(e) => setEd({ ...ed, maxDose: e.target.value })}
                type="number"
                min="0"
                step="0.01"
                style={{ gridColumn: '1 / -1' }}
              />
            </>
          ) : (
            <input
              className="modal-input-sm"
              placeholder="מינון"
              value={ed.dosage || ''}
              onChange={(e) => setEd({ ...ed, dosage: e.target.value })}
              style={{ gridColumn: '1 / -1' }}
            />
          )}
          <input
            className="modal-input-sm"
            placeholder="תדירות (e.g. q8h)"
            value={ed.frequency || ''}
            onChange={(e) => setEd({ ...ed, frequency: e.target.value })}
          />
          <input
            className="modal-input-sm"
            placeholder="אופן מתן (e.g. PO, IV)"
            value={ed.howToGive || ''}
            onChange={(e) => setEd({ ...ed, howToGive: e.target.value })}
          />
          <input
            className="modal-input-sm"
            placeholder="משך (e.g. 5 days)"
            value={ed.duration || ''}
            onChange={(e) => setEd({ ...ed, duration: e.target.value })}
            style={{ gridColumn: '1 / -1' }}
          />
          <input
            className="modal-input-sm"
            placeholder="הערה"
            value={ed.comment || ''}
            onChange={(e) => setEd({ ...ed, comment: e.target.value })}
            style={{ gridColumn: '1 / -1' }}
          />
        </Box>
        {showHebrewWarning && (
          <Typography sx={{ color: '#f57c00', fontSize: '0.85rem', mb: 1.5, direction: 'rtl' }}>
            תרופות מוצגות טוב יותר כשהן כתובות באנגלית
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <IconButton size="small" onClick={handleSave} sx={{ bgcolor: '#103C6E', color: '#fff', '&:hover': { bgcolor: '#0d2e54' }, borderRadius: '8px' }}>
            <FaCheck />
          </IconButton>
          <IconButton size="small" onClick={() => { setEd(drug); setEditing(false); }} sx={{ border: '1px solid #dee2e6', borderRadius: '8px' }}>
            <FaTimes />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      bgcolor: '#fff',
      p: 2,
      borderRadius: '6px',
      border: '1px solid #dee2e6',
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      justifyContent: 'space-between',
      direction: 'rtl',
      minWidth: 0,
      '&:hover': { boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }
    }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ color: '#103C6E', fontSize: '1rem', lineHeight: 1.5 }}>
          <strong>{getDrugDisplayName(drug.drugId)}:</strong>{" "}
          <span dir="auto">
            {dosageDisplay}
            {drug.frequency && ` X ${drug.frequency}`}
            {drug.duration && `, ${drug.duration}`}
            {drug.howToGive && ` (${drug.howToGive})`}
          </span>
        </Typography>
        {drug.comment && (
          <Typography sx={{ color: '#103C6E', fontSize: '0.9rem', fontStyle: 'italic', opacity: 0.7, mt: 0.5, pr: 2 }}>
            {drug.comment}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
        <IconButton size="small" onClick={() => setEditing(true)} sx={{ color: '#103C6E' }}>
          <EditIcon />
        </IconButton>
        <IconButton size="small" onClick={onDelete} sx={{ color: '#103C6E' }}>
          <DeleteIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

const TreatmentSection = ({
  title,
  options,
  drugs,
  getDrugDisplayName,
  onAddOption,
  onUpdateOption,
  onDeleteOption,
  onAddDrug,
  onUpdateDrug,
  onDeleteDrug
}) => {
  const newOptionRef = useRef(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuOptIndex, setMenuOptIndex] = useState(null);

  const handleMenuOpen = (e, optIndex) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuOptIndex(optIndex);
  };
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuOptIndex(null);
  };

  const handleAddOption = () => {
    onAddOption();
    setTimeout(() => {
      if (newOptionRef.current) {
        newOptionRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
        <Typography sx={{ color: '#103C6E', fontWeight: 600, fontSize: '1.05rem' }}>
          {title}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FaPlus />}
          onClick={handleAddOption}
          className="add-option-btn"
          sx={{
            borderColor: '#103C6E',
            color: '#103C6E',
            borderRadius: '8px',
            gap: '5px',
            flexShrink: 0,
          }}
        >
          <span>הוסף אפשרות</span>
        </Button>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { direction: 'rtl', minWidth: 180 } }}
        >
          <MenuItem onClick={() => { onAddDrug(menuOptIndex); handleMenuClose(); }}>
            <ListItemIcon sx={{ minWidth: 32 }}><FaPlus size={14} color="#103C6E" /></ListItemIcon>
            <ListItemText primary="הוסף תרופה" primaryTypographyProps={{ color: '#103C6E', fontFamily: 'Rubik, sans-serif' }} />
          </MenuItem>
          <MenuItem onClick={() => { onDeleteOption(menuOptIndex); handleMenuClose(); }} sx={{ color: '#FF2D55' }}>
            <ListItemIcon sx={{ minWidth: 32 }}><img src={trashIcon} alt="" style={{ width: 16, height: 16, objectFit: 'contain' }} /></ListItemIcon>
            <ListItemText primary="מחיקה" primaryTypographyProps={{ color: '#FF2D55', fontFamily: 'Rubik, sans-serif' }} />
          </MenuItem>
        </Menu>

        {options.map((option, optIndex) => (
          <Box
            key={optIndex}
            ref={optIndex === options.length - 1 ? newOptionRef : null}
            sx={{
              bgcolor: '#fff',
              p: { xs: 1.5, sm: 2.5 },
              borderRadius: '8px',
              border: '2px solid #e0e0e0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              minWidth: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
              <Typography sx={{ color: '#103C6E', fontWeight: 600, fontSize: '0.95rem' }}>
                אפשרות {optIndex + 1}
              </Typography>
              <IconButton size="small" onClick={(e) => handleMenuOpen(e, optIndex)} sx={{ color: '#103C6E' }}>
                <span style={{ fontSize: '1.2rem', fontWeight: 700, letterSpacing: '1px' }}>⋮</span>
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {(option.drugs || []).map((drug, drugIndex) => (
                <DrugCard
                  key={drugIndex}
                  drug={drug}
                  drugs={drugs}
                  getDrugDisplayName={getDrugDisplayName}
                  onUpdate={(updatedDrug) => onUpdateDrug(optIndex, drugIndex, updatedDrug)}
                  onDelete={() => onDeleteDrug(optIndex, drugIndex)}
                  isLastDrug={drugIndex === (option.drugs || []).length - 1}
                />
              ))}
              {(option.drugs || []).length === 0 && (
                <Typography sx={{ color: '#103C6E', opacity: 0.5, fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                  טרם הוגדרו תרופות
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const ProtocolCard = ({ protocol, protocolIndex, categories, getDrugDisplayName, onEdit, onDelete }) => {
  const category = categories.find(c => c.id === protocol.category);
  const diagCount = protocol.diagnoses?.length || 0;

  return (
    <Box sx={{
      bgcolor: '#fff',
      p: 3,
      borderRadius: '10px',
      border: '1px solid #dee2e6',
      mb: 2,
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'all 0.2s',
      '&:hover': { boxShadow: '0 4px 12px rgba(16,60,110,0.15)' }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', direction: 'rtl' }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ color: '#103C6E', fontWeight: 600, fontSize: '1.2rem', mb: 1 }}>
            {protocol.displayName || `פרוטוקול ${protocolIndex + 1}`}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {category && (
              <Chip
                label={category.name}
                size="small"
                sx={{ bgcolor: 'rgba(16,60,110,0.1)', color: '#103C6E', fontWeight: 600 }}
              />
            )}
            {diagCount > 0 && (
              <Chip
                label={diagCount === 1 ? 'אבחנה אחת' : `${diagCount} אבחנות`}
                size="small"
                sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}
              />
            )}
          </Box>

          {protocol.comment && (
            <Typography sx={{ color: '#103C6E', fontSize: '0.9rem', fontStyle: 'italic', opacity: 0.6 }}>
              {protocol.comment}
            </Typography>
          )}
        </Box>
        <div className="formula-actions">
          <div className="action-icon download-icon" onClick={() => exportProtocolToDocx(protocol, getDrugDisplayName)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FaDownload size={18} color="#103C6E" />
          </div>
          <div className="action-icon edit-icon" onClick={onEdit}>
            <EditIcon />
          </div>
          <div className="action-icon delete-icon" onClick={onDelete}>
            <DeleteIcon />
          </div>
        </div>
      </Box>
    </Box>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const ProtocolsSection = ({ protocols, setProtocols, drugs, categories, blacklists, getDrugDisplayName, saveAntibiogramData, saving, onEditingChange, onFocusChange }) => {
  const [currentView, setCurrentView] = useState('list'); // 'list' | 'protocol-edit' | 'diagnosis-edit'
  
  useEffect(() => {
    if (onEditingChange) {
      onEditingChange(currentView !== 'list');
    }
  }, [currentView, onEditingChange]);

  const [editingProtocolIndex, setEditingProtocolIndex] = useState(null);
  const [editingProtocol, setEditingProtocol] = useState(null);
  
  const [editingDiagnosisIndex, setEditingDiagnosisIndex] = useState(null);
  const [editingDiagnosis, setEditingDiagnosis] = useState(null);

  useEffect(() => {
    if (onFocusChange) {
      if (currentView === 'diagnosis-edit') {
        const p = { ...editingProtocol, diagnoses: editingProtocol.diagnoses.map((d, i) => i === editingDiagnosisIndex ? editingDiagnosis : d) };
        onFocusChange(p, editingDiagnosis);
      } else if (currentView === 'protocol-edit') {
        onFocusChange(editingProtocol, null);
      } else {
        onFocusChange(null, null);
      }
    }
  }, [currentView, editingProtocol, editingDiagnosis, editingDiagnosisIndex, onFocusChange]);

  const [activeTab, setActiveTab] = useState('details'); // 'details' | 'diagnoses'
  const [activeDiagTab, setActiveDiagTab] = useState('details'); // 'details' | 'treatment'
  
  const [addingProtocol, setAddingProtocol] = useState(false);
  const [newProto, setNewProto] = useState({ displayName: '', category: '', description: '', comment: '' });
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null); // { type: 'protocol'|'diagnosis', index: number }
  const [showDeleteError, setShowDeleteError] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // --- Handlers for List View ---

  const handleEditProtocol = (index) => {
    setEditingProtocolIndex(index);
    setEditingProtocol(JSON.parse(JSON.stringify(protocols[index])));
    setCurrentView('protocol-edit');
    setActiveTab('details');
  };

  const handleAddProtocol = () => {
    if (!newProto.displayName.trim()) return;
    const newProtocol = {
      id: Date.now().toString(),
      ...newProto,
      diagnoses: []
    };
    setNewProto({ displayName: '', category: '', description: '', comment: '' });
    setAddingProtocol(false);
    setEditingProtocolIndex(protocols.length);
    setEditingProtocol(newProtocol);
    setCurrentView('protocol-edit');
    setActiveTab('details');
  };

  const handleDeleteClick = (type, index) => {
    setItemToDelete({ type, index });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false);
    const { type, index } = itemToDelete;
    
    if (type === 'protocol') {
      setDeletingIndex(index);
      try {
        const updated = protocols.filter((_, i) => i !== index);
        const success = await saveAntibiogramData({ protocols: updated });
        if (success) {
          setProtocols(updated);
          setItemToDelete(null);
        } else {
          setDeleteErrorMessage('שגיאה במחיקת הפרוטוקול');
          setShowDeleteError(true);
        }
      } catch (error) {
        setDeleteErrorMessage('שגיאה במחיקת הפרוטוקול');
        setShowDeleteError(true);
      } finally {
        setDeletingIndex(null);
      }
    } else if (type === 'diagnosis') {
      try {
        const updatedDiagnoses = editingProtocol.diagnoses.filter((_, i) => i !== index);
        setEditingProtocol(prev => ({ ...prev, diagnoses: updatedDiagnoses }));
        setItemToDelete(null);
      } catch (error) {
        setDeleteErrorMessage('שגיאה במחיקת האבחנה');
        setShowDeleteError(true);
      }
    }
  };

  // --- Handlers for Protocol Edit View ---

  const handleSaveProtocol = async () => {
    let arr = [...protocols];
    if (editingProtocolIndex !== null) {
      if (editingProtocolIndex === protocols.length) {
        arr.push(editingProtocol);
      } else {
        arr[editingProtocolIndex] = editingProtocol;
      }
    }
    const success = await saveAntibiogramData({ protocols: arr });
    if (success) {
      setProtocols(arr);
      setCurrentView('list');
      setEditingProtocol(null);
      setEditingProtocolIndex(null);
    }
  };

  const handleCancelProtocol = () => {
    setCurrentView('list');
    setEditingProtocol(null);
    setEditingProtocolIndex(null);
  };

  const handleUpdateProtocolField = (field, value) => {
    setEditingProtocol({ ...editingProtocol, [field]: value });
  };

  const handleAddDiagnosis = () => {
    const newDiag = {
      id: Date.now().toString(),
      displayName: '',
      comment: '',
      recommendations: [],
      specialCases: [],
      treatment: { firstLine: { options: [] }, alternative: { options: [] } }
    };
    const newIndex = (editingProtocol.diagnoses || []).length;
    setEditingProtocol(prev => ({ ...prev, diagnoses: [...(prev.diagnoses || []), newDiag] }));
    
    // Jump straight to editing the new diagnosis
    setEditingDiagnosisIndex(newIndex);
    setEditingDiagnosis(newDiag);
    setActiveDiagTab('details');
    setCurrentView('diagnosis-edit');
  };

  const handleEditDiagnosis = (index) => {
    setEditingDiagnosisIndex(index);
    setEditingDiagnosis(JSON.parse(JSON.stringify(editingProtocol.diagnoses[index])));
    setActiveDiagTab('details');
    setCurrentView('diagnosis-edit');
  };

  // --- Handlers for Diagnosis Edit View ---

  const handleSaveDiagnosis = () => {
    const updatedDiagnoses = [...(editingProtocol.diagnoses || [])];
    updatedDiagnoses[editingDiagnosisIndex] = editingDiagnosis;
    setEditingProtocol(prev => ({ ...prev, diagnoses: updatedDiagnoses }));
    
    // Go back to protocol edit
    setCurrentView('protocol-edit');
    setEditingDiagnosis(null);
    setEditingDiagnosisIndex(null);
    setActiveTab('diagnoses');
  };

  const handleCancelDiagnosis = () => {
    setCurrentView('protocol-edit');
    setEditingDiagnosis(null);
    setEditingDiagnosisIndex(null);
  };

  const updateDiagnosis = (updater) => {
    setEditingDiagnosis(typeof updater === 'function' ? updater(editingDiagnosis) : updater);
  };

  const addListItem = (field, value) => {
    if (!value.trim()) return;
    updateDiagnosis(d => ({ ...d, [field]: [...(d[field] || []), value.trim()] }));
  };

  const deleteListItem = (field, index) => {
    updateDiagnosis(d => ({ ...d, [field]: (d[field] || []).filter((_, i) => i !== index) }));
  };

  const addTreatmentOption = (treatmentType) => {
    updateDiagnosis(diag => {
      const treatment = { firstLine: { options: [] }, alternative: { options: [] }, ...(diag.treatment || {}) };
      const newDrug = { drugId: '', dosage: '', frequency: '', duration: '', howToGive: '', comment: '' };
      treatment[treatmentType] = { options: [...(treatment[treatmentType]?.options || []), { drugs: [newDrug] }] };
      return { ...diag, treatment };
    });
  };

  const updateTreatmentOption = (treatmentType, optIndex, updater) => {
    updateDiagnosis(diag => {
      const treatment = { ...diag.treatment };
      const options = [...(treatment[treatmentType]?.options || [])];
      options[optIndex] = typeof updater === 'function' ? updater(options[optIndex]) : updater;
      treatment[treatmentType] = { ...treatment[treatmentType], options };
      return { ...diag, treatment };
    });
  };

  const deleteTreatmentOption = (treatmentType, optIndex) => {
    updateDiagnosis(diag => {
      const treatment = { ...diag.treatment };
      treatment[treatmentType] = {
        ...treatment[treatmentType],
        options: (treatment[treatmentType]?.options || []).filter((_, i) => i !== optIndex)
      };
      return { ...diag, treatment };
    });
  };

  const addDrugToOption = (treatmentType, optIndex) => {
    const newDrug = { drugId: '', dosage: '', frequency: '', duration: '', howToGive: '', comment: '' };
    updateTreatmentOption(treatmentType, optIndex, opt => ({
      ...opt,
      drugs: [...(opt.drugs || []), newDrug]
    }));
  };

  const updateDrug = (treatmentType, optIndex, drugIndex, updatedDrug) => {
    updateTreatmentOption(treatmentType, optIndex, opt => {
      const drugs = [...(opt.drugs || [])];
      drugs[drugIndex] = updatedDrug;
      return { ...opt, drugs };
    });
  };

  const deleteDrug = (treatmentType, optIndex, drugIndex) => {
    updateTreatmentOption(treatmentType, optIndex, opt => ({
      ...opt,
      drugs: (opt.drugs || []).filter((_, i) => i !== drugIndex)
    }));
  };

  // ─── Renderers ─────────────────────────────────────────────────────────────

  if (currentView === 'list') {
    return (
      <section className="antibiogram-section">
        <div className="tab-description">
          ניהול פרוטוקולי הטיפול האנטיביוטי. כל פרוטוקול מכיל אבחנות, ולכל אבחנה מוגדרות אפשרויות טיפול בקו ראשון ואלטרנטיבי.
        </div>

        <div className="drug-search-wrapper">
          <FaSearch className="drug-search-icon" size={16} />
          <input
            className="drug-search-input"
            type="text"
            placeholder="חיפוש פרוטוקול"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            dir="rtl"
          />
          <button className="drug-search-clear" onClick={() => setSearchQuery('')} aria-label="נקה חיפוש">
            <FaTimes size={14} />
          </button>
        </div>

        {addingProtocol && (
          <div className="formula-item editing" style={{ marginBottom: '24px' }}>
            <div className="formula-edit-form">
              <div className="proto-field-group">
                <label className="proto-field-label">
                  <img src={personIcon} alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />
                  שם הפרוטוקול
                </label>
                <input
                  className="proto-field-input"
                  value={newProto.displayName}
                  onChange={(e) => setNewProto(p => ({ ...p, displayName: e.target.value }))}
                  dir="rtl"
                  autoFocus
                />
              </div>
              <div className="proto-field-group">
                <label className="proto-field-label">
                  <img src={personIcon} alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />
                  קטגוריה
                </label>
                <select
                  className="proto-field-input"
                  value={newProto.category}
                  onChange={(e) => setNewProto(p => ({ ...p, category: e.target.value }))}
                  dir="rtl"
                >
                  <option value="">בחר קטגוריה</option>
                  {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
              </div>
              <div className="proto-field-group">
                <label className="proto-field-label">
                  <img src={calendarIcon} alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />
                  הערה
                </label>
                <input
                  className="proto-field-input"
                  value={newProto.comment}
                  onChange={(e) => setNewProto(p => ({ ...p, comment: e.target.value }))}
                  placeholder="הערה או המלצה שתוצג לרופא מתחת לכל רשימת התרופות"
                  dir="rtl"
                />
              </div>
              <div className="formula-edit-actions">
                <Button
                  variant="contained"
                  onClick={handleAddProtocol}
                  disabled={!newProto.displayName.trim()}
                  sx={{ bgcolor: '#103C6E', '&:hover': { bgcolor: '#0d2e54' }, borderRadius: '8px', padding: '8px 30px' }}
                >
                  הוסף פרוטוקול
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => { setAddingProtocol(false); setNewProto({ displayName: '', category: '', description: '', comment: '' }); }}
                  sx={{ borderColor: '#103C6E', color: '#103C6E', borderRadius: '8px', padding: '8px 30px', marginRight: '12px' }}
                >
                  בטל
                </Button>
              </div>
            </div>
          </div>
        )}

        <button className="add-drug-fab" onClick={() => setAddingProtocol(true)} aria-label="הוסף פרוטוקול">
          <FaPlus size={20} />
        </button>

        <div className="proto-list">
          {protocols.filter(p => !searchQuery || p.displayName?.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && <div className="proto-empty-state">טרם הוגדרו פרוטוקולים</div>}
          {protocols.map((protocol, index) => {
            if (searchQuery && !protocol.displayName?.toLowerCase().includes(searchQuery.toLowerCase())) return null;
            return deletingIndex === index ? (
              <Box key={`loader-${index}`} sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress sx={{ color: '#103C6E' }} />
              </Box>
            ) : (
              <ProtocolCard
                key={protocol.id || index}
                protocol={protocol}
                protocolIndex={index}
                categories={categories}
                getDrugDisplayName={getDrugDisplayName}
                onEdit={() => handleEditProtocol(index)}
                onDelete={() => handleDeleteClick('protocol', index)}
              />
            );
          })}
        </div>

        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title={itemToDelete?.type === 'protocol' ? "האם אתה בטוח שברצונך למחוק פרוטוקול זה?" : "האם אתה בטוח שברצונך למחוק אבחנה זו?"}
          errorMessage={deleteErrorMessage}
          showError={showDeleteError}
          onCloseError={() => setShowDeleteError(false)}
        />
      </section>
    );
  }

  // --- Edit Views ---

  return (
    <Box sx={{ display: 'flex', gap: 4, mt: 2, height: '100%', direction: 'rtl' }}>
      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
        
        {/* Breadcrumbs */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, direction: 'rtl' }}>
            <Link underline="hover" onClick={currentView === 'diagnosis-edit' ? handleCancelDiagnosis : handleCancelProtocol} sx={{ cursor: 'pointer', color: '#103C6E', fontWeight: 600, fontSize: '1rem' }}>
              אנטיביוגרם
            </Link>
            <FaChevronLeft size={12} color="#103C6E" />
            <Typography sx={{ color: '#103C6E', fontWeight: 600, fontSize: '1rem' }}>
              {currentView === 'diagnosis-edit' ? 'עריכת אבחנה' : 'עריכת פרוטוקול'}
            </Typography>
          </Box>
          <Box sx={{ bgcolor: '#EEFFFD', p: 2, borderRadius: '8px', border: '1px solid rgba(12,170,152,0.2)', direction: 'rtl' }}>
            <Typography sx={{ color: '#103C6E', fontWeight: 700, fontSize: '1rem' }}>
              {editingProtocol.displayName || 'פרוטוקול חדש'}
            </Typography>
          </Box>
        </Box>

        {(currentView === 'protocol-edit' || currentView === 'diagnosis-edit') && (
          <>
            <Box sx={{ display: 'flex', gap: 2, borderBottom: '2px solid #dee2e6' }}>
              <Button
                onClick={() => { setActiveTab('details'); if (currentView === 'diagnosis-edit') { handleCancelDiagnosis(); } }}
                sx={{ fontSize: '1.1rem', fontWeight: 600, color: (activeTab === 'details' && currentView !== 'diagnosis-edit') ? '#103C6E' : '#6c757d', borderBottom: (activeTab === 'details' && currentView !== 'diagnosis-edit') ? '3px solid #0CAA98' : '3px solid transparent', borderRadius: 0, pb: 1, mb: '-2px', gap: '6px' }}
              >
                <img src={thermometerIcon} alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />
                פרטי פרוטוקול
              </Button>
              <Button
                onClick={() => { setActiveTab('diagnoses'); if (currentView === 'diagnosis-edit') { handleCancelDiagnosis(); } }}
                sx={{ fontSize: '1.1rem', fontWeight: 600, color: (activeTab === 'diagnoses' || currentView === 'diagnosis-edit') ? '#103C6E' : '#6c757d', borderBottom: (activeTab === 'diagnoses' || currentView === 'diagnosis-edit') ? '3px solid #0CAA98' : '3px solid transparent', borderRadius: 0, pb: 1, mb: '-2px', gap: '6px' }}
              >
                <img src={layoutIcon} alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />
                אבחנות ({(editingProtocol.diagnoses || []).length})
              </Button>
            </Box>

            {activeTab === 'details' && (
              <div style={{ background: '#fff', padding: '24px', borderRadius: '8px', border: '1px solid #D6E2F0' }}>
                {[
                  { icon: personIcon, label: 'שם הפרוטוקול', field: 'displayName', type: 'input', placeholder: '' },
                  { icon: personIcon, label: 'קטגוריה', field: 'category', type: 'select', placeholder: '' },
                  { icon: calendarIcon, label: 'הערה', field: 'comment', type: 'input', placeholder: 'הערה או המלצה שתוצג לרופא מתחת לכל רשימת התרופות' },
                ].map(({ icon, label, field, type, placeholder }) => (
                  <div key={field} style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px', direction: 'rtl' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '1.1rem', fontWeight: 700, color: '#103C6E' }}>
                      <img src={icon} alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />
                      {label}
                    </div>
                    {type === 'select' ? (
                      <select
                        value={editingProtocol[field] || ''}
                        onChange={(e) => handleUpdateProtocolField(field, e.target.value)}
                        dir="rtl"
                        style={{ width: '100%', padding: '10px 14px', paddingLeft: '14px', border: '1px solid rgba(16,60,110,0.3)', borderRadius: '8px', fontSize: '0.95rem', fontFamily: 'Rubik, sans-serif', color: '#103C6E', boxSizing: 'border-box', backgroundColor: '#fff', appearance: 'none', WebkitAppearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23103C6E' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: '14px center' }}
                      >
                        <option value="">בחר קטגוריה</option>
                        {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                      </select>
                    ) : (
                      <input
                        value={editingProtocol[field] || ''}
                        onChange={(e) => handleUpdateProtocolField(field, e.target.value)}
                        placeholder={placeholder}
                        dir="rtl"
                        style={{ width: '100%', padding: '10px 14px', border: '1px solid rgba(16,60,110,0.3)', borderRadius: '8px', fontSize: '0.95rem', fontFamily: 'Rubik, sans-serif', color: '#103C6E', boxSizing: 'border-box' }}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {(activeTab === 'diagnoses' || currentView === 'diagnosis-edit') && (
              <Box sx={{ bgcolor: '#fff', p: 3, borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                {currentView === 'diagnosis-edit' ? (
                  <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {[
                        { icon: personIcon, label: 'שם האבחנה', field: 'displayName' },
                        { icon: calendarIcon, label: 'הערה', field: 'comment' },
                      ].map(({ icon, label, field }) => (
                        <div key={field} style={{ display: 'flex', flexDirection: 'column', gap: '6px', direction: 'rtl' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '1.1rem', fontWeight: 700, color: '#103C6E' }}>
                            <img src={icon} alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />
                            {label}
                          </div>
                          <input
                            value={editingDiagnosis[field] || ''}
                            onChange={(e) => updateDiagnosis(d => ({ ...d, [field]: e.target.value }))}
                            dir="rtl"
                            style={{ width: '100%', padding: '10px 14px', border: '1px solid rgba(16,60,110,0.3)', borderRadius: '8px', fontSize: '0.95rem', fontFamily: 'Rubik, sans-serif', color: '#103C6E', boxSizing: 'border-box' }}
                          />
                        </div>
                      ))}
                      <div style={{ direction: 'rtl', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <ListField title="בדיקות אבחנתיות" items={editingDiagnosis.recommendations || []} onAdd={(val) => addListItem('recommendations', val)} onDelete={(i) => deleteListItem('recommendations', i)} />
                        <ListField title="מצבים מיוחדים" items={editingDiagnosis.specialCases || []} onAdd={(val) => addListItem('specialCases', val)} onDelete={(i) => deleteListItem('specialCases', i)} />
                      </div>
                      {['firstLine', 'alternative'].map(treatmentType => (
                        <TreatmentSection
                          key={treatmentType}
                          title={treatmentType === 'firstLine' ? 'קו ראשון' : 'אלטרנטיבי'}
                          options={editingDiagnosis.treatment?.[treatmentType]?.options || []}
                          drugs={drugs}
                          getDrugDisplayName={getDrugDisplayName}
                          onAddOption={() => addTreatmentOption(treatmentType)}
                          onUpdateOption={(optIndex, updater) => updateTreatmentOption(treatmentType, optIndex, updater)}
                          onDeleteOption={(optIndex) => deleteTreatmentOption(treatmentType, optIndex)}
                          onAddDrug={(optIndex) => addDrugToOption(treatmentType, optIndex)}
                          onUpdateDrug={(optIndex, drugIndex, updatedDrug) => updateDrug(treatmentType, optIndex, drugIndex, updatedDrug)}
                          onDeleteDrug={(optIndex, drugIndex) => deleteDrug(treatmentType, optIndex, drugIndex)}
                        />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                      <Button variant="outlined" onClick={handleCancelDiagnosis} style={cancelButton}>ביטול שינויים</Button>
                      <Button variant="contained" onClick={handleSaveDiagnosis} style={confirmButton}>אישור אבחנה</Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {(editingProtocol.diagnoses || []).map((diag, index) => (
                        <Box key={diag.id || index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, p: 2, borderRadius: '8px', border: '1px solid #dee2e6', '&:hover': { bgcolor: '#f8f9fa', borderColor: '#103C6E' }, cursor: 'pointer', transition: 'all 0.2s' }} onClick={() => handleEditDiagnosis(index)}>
                          <Box>
                            {(() => {
                              const plain = (diag.displayName || `אבחנה ${index + 1}`).replace(/<[^>]+>/g, '');
                              const colonIdx = plain.indexOf(':');
                              const head = colonIdx === -1 ? plain : plain.slice(0, colonIdx + 1);
                              const body = colonIdx === -1 ? '' : plain.slice(colonIdx + 1).trim();
                              return (
                                <Typography sx={{ color: '#103C6E', fontWeight: 400, fontSize: '0.8rem', lineHeight: 1.6 }}>
                                  <Box component="span" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{head}</Box>
                                  {body && ` ${body}`}
                                </Typography>
                              );
                            })()}
                            {diag.comment && (
                              <Typography sx={{ color: '#6c757d', fontSize: '0.9rem', mt: 0.5 }}>{diag.comment}</Typography>
                            )}
                          </Box>
                          <div className="formula-actions">
                            <div className="action-icon delete-icon" onClick={(e) => { e.stopPropagation(); handleDeleteClick('diagnosis', index); }}>
                              <DeleteIcon />
                            </div>
                            <div className="action-icon edit-icon" onClick={(e) => { e.stopPropagation(); handleEditDiagnosis(index); }}>
                              <EditIcon />
                            </div>
                          </div>
                        </Box>
                      ))}
                      {(editingProtocol.diagnoses || []).length === 0 && (
                        <Typography sx={{ color: '#6c757d', textAlign: 'center', py: 4, fontStyle: 'italic' }}>טרם הוגדרו אבחנות</Typography>
                      )}
                    </Box>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                      <button onClick={handleAddDiagnosis} style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#0CAA98', color: '#fff', border: 'none', borderRadius: '24px', padding: '10px 20px', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'Rubik, sans-serif' }}>
                        <FaPlus size={14} />
                        הוסף אבחנה
                      </button>
                    </div>
                  </>
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button variant="outlined" onClick={handleCancelProtocol} style={cancelButton} disabled={saving}>
                ביטול
              </Button>
              <LoadingSubmitButton onClick={handleSaveProtocol} isLoading={saving} text="שמור שינויים" loadingText="שומר..." style={confirmButton} />
            </Box>
          </>
        )}
      </Box>



      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={itemToDelete?.type === 'protocol' ? "האם אתה בטוח שברצונך למחוק פרוטוקול זה?" : "האם אתה בטוח שברצונך למחוק אבחנה זו?"}
        errorMessage={deleteErrorMessage}
        showError={showDeleteError}
        onCloseError={() => setShowDeleteError(false)}
      />
    </Box>
  );
};

export default ProtocolsSection;
